import { AuthContainer } from "../container/AuthContainer.tsx";
import { useVerificationCode } from "../hooks/useVerificationCode.ts";
import { useEffect, useState } from "react";
import {
    SEARCH_PARAM_CODE,
    SEARCH_PARAM_INDEX
} from "../../../src/shared";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { IdentityServiceClient } from "@tintoa/identity-service-client";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CheckCircle, Error } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

enum DeletionStatus {
    PENDING,
    SUCCESS,
    ERROR
}

export const DeletionConfirmationScreen = () => {
    const { index, code } = useVerificationCode();
    const navigate = useNavigate();
    const [deletionStatus, setDeletionStatus] =
        useState<DeletionStatus>(DeletionStatus.PENDING);
    const loginUrl = "/app/login";

    useEffect(() => {
        const url = new URL("/auth/confirm-deletion", window.location.href);
        url.searchParams.append(SEARCH_PARAM_CODE, code);
        url.searchParams.append(SEARCH_PARAM_INDEX, index);

        fetchWithError<{ success: boolean }>(url)
            .then(async (res) => {
                if (res.success) {
                    try {
                        const client = new IdentityServiceClient(window.location.origin);
                        await client.Session("").logout();
                    } catch (logoutError) {
                        console.error("Logout after deletion failed:", logoutError);
                    }
                    setDeletionStatus(DeletionStatus.SUCCESS);
                } else {
                    setDeletionStatus(DeletionStatus.ERROR);
                }
            })
            .catch((error) => {
                console.error("Deletion confirmation error:", error);
                setDeletionStatus(DeletionStatus.ERROR);
            });
    }, [index, code]);

    const handleGoToLogin = () => {
        navigate(loginUrl);
    };

    return (
        <AuthContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column"
                }}
            >
                {deletionStatus === DeletionStatus.PENDING && (
                    <>
                        <Typography>Lösche Zugang . . .</Typography>
                        <CircularProgress size="3rem" sx={{ my: 3 }} />
                    </>
                )}
                {deletionStatus === DeletionStatus.SUCCESS && (
                    <>
                        <CheckCircle
                            color={"success"}
                            fontSize={"large"}
                            sx={{ my: 3 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            Dein Zugang wurde erfolgreich gelöscht
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                            Alle deine Daten wurden unwiderruflich entfernt.
                        </Typography>
                        <Button variant="contained" onClick={handleGoToLogin} sx={{ my: 3 }}>
                            Zum Login
                        </Button>
                    </>
                )}
                {deletionStatus === DeletionStatus.ERROR && (
                    <>
                        <Error
                            color={"error"}
                            fontSize={"large"}
                            sx={{ my: 3 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            Bei der Löschung ist ein Fehler aufgetreten
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                            Der Bestätigungslink ist möglicherweise abgelaufen oder ungültig.
                            Bitte wende dich an support@tintoa.net, wenn das Problem weiterhin besteht.
                        </Typography>
                        <Button variant="outlined" onClick={handleGoToLogin} sx={{ my: 3 }}>
                            Zum Login
                        </Button>
                    </>
                )}
            </Box>
        </AuthContainer>
    );
};

